import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { useLocation } from "react-router-dom";
import moment from "moment";
import { useSelector } from "react-redux";
import { hotelQueryParams } from "./queryParamUtils";
import { getUserToken } from "../../../helper/commonFunction";
import { ApiCall } from "../../../helper/axios";
import { normalizeFacetValue } from "./useFilterHotels";
import { getHeaders } from "./useHotelDataMerger";

const INITIAL_VISIBLE = 10;
const PAGE_VISIBLE_STEP = 10;
const PREFETCH_OFFSET = 30;

export const defaultFilterSelection = {
  price: {},
  propertyType: {},
  hotelName: "",
  ratings: {},
  priceSelection: {},
};

export const useGetListHotels = (callAPI = true) => {
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const { agentActiveCurrency, hotelCompanyList } = useSelector((state) => state.userData);
  const [apiCompleted, setAPICompleted] = useState(false)

  const [loading, setLoading] = useState(true);
  const [mainLoading, setmainLoading] = useState(false)
  const [allLoading, setallLoading] = useState(true)
  const [modifySearch, showModifySearch] = useState(false);
  const [selected, setSelected] = useState(defaultFilterSelection);
  const [sort, setSort] = useState({ price: null, mostPopular: false });

  const [allHotels, setAllHotels] = useState([]);
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);
  const [lastHid, setLastHid] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const basePayloadRef = useRef(null);
  const searchTokenRef = useRef(0);
  const reconcilingRef = useRef(false); // true while phase 2 (HotelRack merge) is in flight
  const autoLoadsRef = useRef(0);

  const {
    city,
    hid,
    ratings,
    nationality,
    countryOfResidence,
    roomInfo,
    checkinDate,
    checkoutDate,
    fsc,
    cityname, searchData, residence, isHotel
  } = hotelQueryParams(location);



  const searchKeyword = query.get("keyword")?.trim().toLowerCase();
  const [header, setHeaders] = useState({})
  useEffect(() => {
    const fetchHeaders = async () => {
      const res = await getHeaders(
        searchData,
        query.get("hid"),
        query.get("cid"),
        isHotel
      );
      setHeaders(res);
    };


    fetchHeaders();
  }, [location.search]);

  useEffect(() => {
    const obj = {};
    ratings?.forEach?.((r) => {
      if (r) obj[r] = true;
    });
    setSelected((prev) => ({ ...prev, ratings: obj }));
  }, [ratings?.length]);

  const roomLine = `${roomInfo?.length} Rooms 
  ${roomInfo?.reduce((acc, i) => acc + +i?.noOfAdult, 0)} 
  Adults ${roomInfo?.reduce((acc, i) => acc + +i?.noOfChild, 0)} Child`;

  const paramObj = {};
  for (var value of query.keys()) {
    paramObj[value] = query.get(value);
  }
  const queryString = Object.keys(paramObj)
    .map((k) => `${k}=${query.get(k)}`)
    .join("&");

  const normalizeText = (text = "") =>
    text
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "")
      .trim();

  const buildHotelKey = (hotel) => {
    const name = normalizeText(hotel?.name);
    const address = normalizeText(hotel?.address || "");
    const city = normalizeText(hotel?.cityName || "");
    return `${name}_${address}_${city}`;
  };

  const refreshList = async () => {
    setallLoading(true);
    reconcilingRef.current = true;
    setAPICompleted(false);
    setLoading(true);
    setmainLoading(false);
    setAllHotels([]);
    setVisibleCount(INITIAL_VISIBLE);
    setLastHid(0);
    setHasMore(false);
    setmainLoading(false);
    setAllHotels([]);
    setVisibleCount(INITIAL_VISIBLE);
    setLastHid(0);
    setHasMore(false);
    setSelected(defaultFilterSelection);
    setSort({ price: null, mostPopular: false });
    autoLoadsRef.current = 0;

    const allowedHotelVendors = (hotelCompanyList && hotelCompanyList.length) ? hotelCompanyList.map((v) => String(v).toUpperCase()) : ["DAHRTOURS", "HOTELRACK", "RATEHAWK"];
    // const allowedHotelVendors = ['RATEHAWK']
    const searchVendors = allowedHotelVendors.filter((v) => v === "HOTELRACK" || v === "RATEHAWK");
    const wantDahrtours = allowedHotelVendors.includes("DAHRTOURS");
    const wantRateHawk = searchVendors.includes("RATEHAWK");
    const wantHotelRack = searchVendors.includes("HOTELRACK");

    const mergedPayload = {
      source: "RATEHAWK",
      vendors: searchVendors,
      cityCode: searchData?.rateHawkCityCode,
      cityId: city,
      hid,
      nationalityId: nationality, // HotelRack needs a nationality id for pricing
      checkinDate: moment(checkinDate).format("YYYY-MM-DD"),
      checkoutDate: moment(checkoutDate).format("YYYY-MM-DD"),
      residence,
      roomInfo,
      lastHid: 0,
    };
    basePayloadRef.current = mergedPayload;

    // PROGRESSIVE LOAD. RateHawk is fast, HotelRack is slow. Render RateHawk the
    // instant it lands (phase 1), then fold HotelRack in via the reconcile
    // endpoint (phase 2) — which runs HotelRack ONCE and merges it against the
    // RateHawk hotels we already have (cheapest-wins on the server). No barrier
    // on the slow vendor for first paint, and RateHawk is never called twice.
    const token = getUserToken();
    const myToken = ++searchTokenRef.current;
    const url = `${process.env.client_apiEndpoint}/hotels/search-hotels-paginated`;
    const hit = async (payload) => {
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error("Failed to fetch hotels");
      return response.json();
    };

    try {
      // Phase 0 — own inventory (fast, from our DB), shown first.
      let ownHotels = [];
      if (wantDahrtours) {
        try {
          const ownData = await hit({ ...mergedPayload, source: "DAHRTOURS", vendors: ["DAHRTOURS"] });
          if (myToken !== searchTokenRef.current) return;
          ownHotels = Array.isArray(ownData?.data?.hotels) ? ownData.data.hotels : [];
          if (ownHotels.length) {
            setAllHotels(ownHotels);
            setLoading(false);
            setmainLoading(false);
          }
        } catch (ownErr) {
          console.error("Own inventory phase error:", ownErr);
        }
      }

      // Phase 1 — RateHawk, appended after own inventory.
      let rhHotels = [];
      let rhLastHid = 0;
      let rhHasMore = false;
      if (wantRateHawk) {
        const rhData = await hit({ ...mergedPayload, vendors: ["RATEHAWK"], source: "RATEHAWK" });
        if (myToken !== searchTokenRef.current) return;

        rhHotels = Array.isArray(rhData?.data?.hotels)
          ? rhData.data.hotels.map((h) => ({
            ...h,
            cheapestVendor: "RATEHAWK",
            vendors: [{ vendor: "RATEHAWK", amount: Number(h?.amount) }],
          }))
          : [];
        rhLastHid = rhData?.data?.lastHid ?? 0;
        rhHasMore = Boolean(rhData?.data?.hasMore);
      }

      if (rhHotels.length || ownHotels.length) {
        setAllHotels([...ownHotels, ...rhHotels]);
        setLastHid(rhLastHid);
        setHasMore(rhHasMore);
        setLoading(false); // list is usable now; HotelRack still merging
        setAPICompleted(true); // cards + reviews load from RateHawk right away
      }

      // Phase 2 — reconcile: run HotelRack once and merge into the RateHawk
      // hotels above. Runs in the background; results replace the list.
      if (wantHotelRack) hit({ ...mergedPayload, reconcile: true, rhHotels, rhLastHid, rhHasMore })
        .then((mergedData) => {
          if (myToken !== searchTokenRef.current) return;
          if (mergedData?.data?.hotels) {
            setAllHotels([...ownHotels, ...mergedData.data.hotels]);
            setLastHid(mergedData.data.lastHid || 0);
            setHasMore(mergedData.data.hasMore || false);
          }
        })
        .catch((err) => {
          console.error("Hotel reconcile (HotelRack) error:", err);
          if (myToken === searchTokenRef.current) {
            setLastHid(rhLastHid || 0);
            setHasMore(rhHasMore || false);
          }
        })
        .finally(() => {
          if (myToken !== searchTokenRef.current) return;
          reconcilingRef.current = false;
          setLoading(false);
          setAPICompleted(true);
          setallLoading(false);
          setmainLoading(false);
        });
      if (!wantHotelRack) {
        reconcilingRef.current = false;
        setLoading(false);
        setAPICompleted(true);
        setallLoading(false);
        setmainLoading(false);
      }
    } catch (err) {
      console.error("Hotel search error (RateHawk phase):", err);
      if (myToken !== searchTokenRef.current) return;
      setLoading(false);
      setAPICompleted(true);
      setallLoading(false);
      setmainLoading(false);
    }
  };

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore || !basePayloadRef.current || reconcilingRef.current) return;

    setLoadingMore(true);
    try {
      const token = getUserToken();
      const response = await fetch(
        `${process.env.client_apiEndpoint}/hotels/search-hotels-paginated`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify({
            ...basePayloadRef.current,
            lastHid,
          }),
        }
      );

      if (!response.ok) throw new Error("Failed to fetch more hotels");

      const data = await response.json();
      if (data?.data?.hotels) {
        setAllHotels((prev) => [...prev, ...data.data.hotels]);
        setLastHid(data.data.lastHid || 0);
        setHasMore(data.data.hasMore || false);
      }
    } catch (err) {
      console.error("Load more error:", err);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, lastHid]);

  // True when the user has narrowed the list with any filter. While a filter is
  // active we reveal already-loaded matches but don't fetch more pages — a
  // filtered result of 1-2 hotels leaves the sentinel in view and would
  // otherwise immediately trigger the next page.
  const hasActiveFilter = () => {
    const anySelected = (obj) => Object.values(obj || {}).some((v) => v?.selected);
    return (
      anySelected(selected?.ratings) ||
      anySelected(selected?.propertyType) ||
      anySelected(selected?.priceSelection) ||
      (selected?.hotelName?.length > 0) ||
      (!!selected?.minPrice && !!selected?.maxPrice)
    );
  };

  const revealMore = useCallback((userScrolled = false) => {
    const newVisibleCount = Math.min(visibleCount + PAGE_VISIBLE_STEP, allHotels.length);
    setVisibleCount(newVisibleCount);

    if (hasActiveFilter()) return;

    if (
      allHotels.length - newVisibleCount <= PAGE_VISIBLE_STEP && // reveal loaded hotels first; fetch next page only near the end
      hasMore &&
      !loadingMore
    ) {
      // Further pages load only on a real user scroll. The initial 2nd-page
      // prefetch is handled by the effect below so it fires even when the first
      // page already fills the screen and the bottom sentinel never appears.
      if (userScrolled) {
        loadMore();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleCount, allHotels, selected, hasMore, loadingMore, loadMore]);

  useEffect(() => {
    if (callAPI) {
      refreshList();
    }
  }, [location.state]);

  // Prefetch the 2nd page once, right after the first results arrive. This is
  // the "2nd of 2" API calls on load, and it fires regardless of whether the
  // bottom sentinel is visible (the first page usually already fills the screen).
  // Any further pages load only on a real user scroll.
  useEffect(() => {
    if (
      callAPI &&
      !loading &&
      apiCompleted &&
      allHotels.length > 0 &&
      allHotels.length <= visibleCount && // only fetch the next page once everything already loaded is revealed
      !reconcilingRef.current && // never page while HotelRack is still merging phase 1
      hasMore &&
      !loadingMore &&
      autoLoadsRef.current < 1 &&
      !hasActiveFilter()
    ) {
      autoLoadsRef.current += 1;
      loadMore();
    }
  }, [callAPI, loading, apiCompleted, allHotels.length, hasMore, loadingMore]);



  const filteredHotels = useMemo(() => {
    const visibleHotels = allHotels.slice(0, visibleCount);

    let result = visibleHotels.filter((h) => {
      return (
        h.amount !== undefined &&
        h.amount !== null &&
        h.amount > 0 &&
        h.propertyType &&
        h.rating !== undefined &&
        h.rating > 0
      );
    });

    const propertyTypeToSelect = Object.keys(selected?.propertyType ?? {}).filter(
      (k) => selected?.propertyType?.[k]?.selected
    );

    if (propertyTypeToSelect?.length > 0) {
      result = result.filter((d) =>
        propertyTypeToSelect.includes(normalizeFacetValue(d?.propertyType))
      );
    }

    if (searchKeyword?.length > 0) {
      const exact = result.filter(
        (h) => h.name?.toLowerCase() === searchKeyword
      );
      const partial = result.filter(
        (h) =>
          h.name?.toLowerCase() !== searchKeyword &&
          h.name?.toLowerCase().includes(searchKeyword)
      );
      const rest = result.filter(
        (h) => !h.name?.toLowerCase().includes(searchKeyword)
      );
      result = [...exact, ...partial, ...rest];
    }

    if (selected?.hotelName?.length > 0) {
      const s = selected.hotelName.toLowerCase();
      const exact = result.filter((h) => h.name?.toLowerCase() === s);
      const partial = result.filter(
        (h) =>
          h.name?.toLowerCase() !== s &&
          h.name?.toLowerCase().includes(s)
      );
      result = [...exact, ...partial];
    }

    const ratingToSelect = Object.keys(selected?.ratings ?? {}).filter(
      (k) => selected?.ratings?.[k]?.selected
    );

    if (ratingToSelect?.length > 0) {
      result = result.filter((d) =>
        ratingToSelect.includes(d?.rating?.toString())
      );
    }

    const priceRangeSelected = Object.keys(
      selected?.priceSelection ?? {}
    ).filter((k) => selected?.priceSelection?.[k]?.selected);

    if (priceRangeSelected?.length > 0) {
      result = result.filter((d) =>
        priceRangeSelected.some(
          (p) =>
            parseFloat(d?.amount) >= selected.priceSelection[p].d.start &&
            parseFloat(d?.amount) <= selected.priceSelection[p].d.end
        )
      );
    }

    if (selected?.minPrice && selected?.maxPrice) {
      result = result.filter(
        (d) =>
          parseFloat(d?.amount) >= selected.minPrice &&
          parseFloat(d?.amount) <= selected.maxPrice
      );
    }

    if (sort.price === "ASC") {
      result.sort((a, b) => (a?.amount || 0) - (b?.amount || 0));
    } else if (sort.price === "DESC") {
      result.sort((a, b) => (b?.amount || 0) - (a?.amount || 0));
    }

    if (sort.mostPopular) {
      result.sort(
        (a, b) => parseInt(b?.rating || 1) - parseInt(a?.rating || 1)
      );
    }

    return result;
  }, [allHotels, visibleCount, selected, sort, searchKeyword]);

  // Memoize by the URL string so the object reference only changes when the
  // search params actually change, not on every render. This is critical for
  // React.memo on HotelListItem (receives filter as searchContext).
  const filterObj = useMemo(() => ({
    city,
    hid,
    ratings,
    nationality,
    countryOfResidence,
    roomInfo,
    checkinDate,
    checkoutDate,
    fsc,
    isHotel,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [location.search]);

  return {
    refreshList,
    loading,
    mainLoading,
    allLoading,
    apiCompleted,
    modifySearch,
    hotels: filteredHotels,
    originalData: allHotels,
    showModifySearch,
    roomLine,
    city,
    ratings,
    nationality,
    roomInfo,
    checkinDate,
    checkoutDate,
    fsc,
    filter: filterObj,
    queryString,
    selected,
    setSelected,
    defaultSelection: defaultFilterSelection,
    sort,
    setSort,
    header,
    loadMore,
    revealMore,
    hasMore,
    loadingMore,
    visibleCount,
    setVisibleCount,
  };
};